import React, { useEffect, useMemo } from 'react';

const ALLOWED_BRIDGE_URL_SCHEMES = new Set([
  'bridge',
  'bridge-dev',
  'bridge-local',
  'bridge-debug-local',
]);
const AUTO_OPEN_DELAY_MS = 180;

function normalizePaymentStatus(status: string | null) {
  return status === 'success' ? 'success' : 'cancel';
}

function readPaymentStatus(search: string) {
  return normalizePaymentStatus(new URLSearchParams(search).get('status'));
}

function normalizeBridgeScheme(scheme: string | null) {
  if (scheme && ALLOWED_BRIDGE_URL_SCHEMES.has(scheme)) {
    return scheme;
  }
  return 'bridge';
}

export function buildOnboardingPaymentCallbackURL(search: string) {
  const params = new URLSearchParams(search);
  const callbackParams = new URLSearchParams();
  callbackParams.set('status', normalizePaymentStatus(params.get('status')));

  const next = params.get('next');
  if (next?.startsWith('/')) {
    callbackParams.set('next', next);
  }

  const checkoutSessionId = params.get('checkout_session_id');
  if (checkoutSessionId) {
    callbackParams.set('checkout_session_id', checkoutSessionId);
  }

  return `${normalizeBridgeScheme(params.get('scheme'))}://onboarding/payment?${callbackParams.toString()}`;
}

export function OnboardingCheckoutReturnPage(): React.JSX.Element {
  const status = useMemo(() => readPaymentStatus(window.location.search), []);
  const callbackURL = useMemo(
    () => buildOnboardingPaymentCallbackURL(window.location.search),
    []
  );
  const title =
    status === 'success' ? 'Checkout Complete' : 'Checkout Cancelled';
  const description =
    status === 'success'
      ? 'Return to Bridge to finish onboarding.'
      : 'Return to Bridge to continue onboarding.';

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      window.location.replace(callbackURL);
    }, AUTO_OPEN_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [callbackURL]);

  return (
    <main className="flex h-[100dvh] items-center justify-center bg-black px-6 text-white">
      <div className="max-w-sm text-center">
        <h1 className="text-2xl font-medium">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-white/58">
          {description}
        </p>
        <p className="mt-2 text-xs text-white/40">
          Bridge should open automatically.
        </p>
        <a
          className="mt-8 inline-flex h-11 items-center justify-center rounded-full bg-white px-6 text-sm font-medium text-black transition hover:bg-white/90"
          href={callbackURL}
        >
          Open Bridge
        </a>
      </div>
    </main>
  );
}
